import { FC, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type {
  CutsheetCatalog,
  Extra,
  ExtrasSelectionQuestion,
} from '../../../types';
import { DropZone } from './drop-zone';
import { SortableItem } from './sortable-item';

export interface ExtrasSelectionContentProps {
  question: ExtrasSelectionQuestion;
  catalog: CutsheetCatalog;
  canAccept: boolean | null;
  draggingType: string | null;
  onAddItem?: (questionId: string, itemId: string) => void;
  onRemoveItem?: (questionId: string, itemId: string) => void;
  onEditItem?: (itemId: string, itemType: 'cut' | 'trim' | 'extra') => void;
  onReorderItems?: (questionId: string, newOrderIds: string[]) => void;
  onAddClick: () => void;
}

/**
 * Content panel for an ExtrasSelection question.
 *
 * Mirrors FreeSelectionContent but scoped to extras only: drop zone accepts
 * only extra items, the sortable list draws from `availableExtraIds`.
 * @param root0
 * @param root0.question
 * @param root0.catalog
 * @param root0.canAccept
 * @param root0.draggingType
 * @param root0.onAddItem
 * @param root0.onRemoveItem
 * @param root0.onEditItem
 * @param root0.onReorderItems
 * @param root0.onAddClick
 */
export const ExtrasSelectionContent: FC<ExtrasSelectionContentProps> = ({
  question,
  catalog,
  canAccept,
  draggingType,
  onAddItem,
  onRemoveItem,
  onEditItem,
  onReorderItems,
  onAddClick,
}) => {
  const selectedExtras = useMemo(
    () =>
      (question.availableExtraIds ?? [])
        .map((id) => catalog.extras.find((e) => e.id === id))
        .filter(Boolean) as Extra[],
    [question.availableExtraIds, catalog.extras],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleReorder = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorderItems) return;
    const ids = question.availableExtraIds ?? [];
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorderItems(question.id, arrayMove(ids, oldIndex, newIndex));
  };

  return (
    <Stack spacing={1}>
      {selectedExtras.length > 0 && (
        <Box>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleReorder}
          >
            <SortableContext
              items={selectedExtras.map((i) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              <Stack spacing={0.5}>
                {selectedExtras.map((item) => (
                  <SortableItem
                    key={item.id}
                    item={item}
                    itemType="extra"
                    onEdit={
                      onEditItem
                        ? () => onEditItem(item.id, 'extra')
                        : undefined
                    }
                    onRemove={
                      onRemoveItem
                        ? () => onRemoveItem(question.id, item.id)
                        : undefined
                    }
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        </Box>
      )}

      {onAddItem && (
        <DropZone
          droppableId={`question-dropzone-${question.id}`}
          droppableData={{ type: 'question-dropzone', questionId: question.id }}
          canAccept={canAccept}
          draggingType={draggingType}
          incompatibleMessage="This question only accepts extras"
          placeholder="Drop or click to add extras"
          onClick={onAddClick}
        />
      )}
    </Stack>
  );
};
